/**
 * Lógica PURA de la papelera (UNDO-TRASH-LIGHT L2) — sin Solid ni PowerSync.
 *
 * La papelera unifica TRES clases de entidades soft-deleted (`deleted_at`):
 *   - notas (`bible_note`),
 *   - resaltados (filas `bible_verse_highlight` agrupadas por su grupo origen
 *     `bible_highlight`, misma agrupación que /highlights),
 *   - marcadores (`bible_bookmark`).
 *
 * Estas funciones son deterministas y testeables en unit: agrupan las filas
 * planas de resaltado borradas y mezclan las tres clases en una sola lista
 * ordenada por fecha de borrado (más reciente primero).
 */
import type { VerseRef } from "@/utils/verse-reference";
import { compareVerseRefs } from "@/utils/verse-collections";

export interface TrashedNoteEntry {
  kind: "note";
  id: string;
  textContent: string | null;
  deletedAt: string | null;
}

export interface TrashedHighlightEntry {
  kind: "highlight";
  /** Clave del grupo: `origin_id` si existe, si no el id de la fila suelta. */
  id: string;
  /** Grupo origen (`bible_highlight.id`) o null para filas sueltas antiguas. */
  originId: string | null;
  /** Filas planas `bible_verse_highlight` del grupo (restore/purge las cubre TODAS). */
  rowIds: string[];
  color: string;
  deletedAt: string | null;
  /** Versículos del grupo en orden canónico. */
  verses: (VerseRef & { version: string })[];
}

export interface TrashedBookmarkEntry {
  kind: "bookmark";
  id: string;
  deletedAt: string | null;
  version: string;
  book: number;
  chapter: number;
  verse: number;
}

export type TrashEntry = TrashedNoteEntry | TrashedHighlightEntry | TrashedBookmarkEntry;

/** Fila plana de `bible_verse_highlight` borrada, tal y como sale de la query. */
export interface TrashedHighlightRow {
  highlightId: string | null;
  originId: string | null;
  color: string | null;
  deletedAt: string | null;
  version: string | null;
  book: number | null;
  chapter: number | null;
  verse: number | null;
}

const DEFAULT_HIGHLIGHT_COLOR = "#eab308";

/**
 * Agrupa filas borradas de `bible_verse_highlight` por su grupo origen
 * (`origin_id`; fallback: la propia fila), igual que hace /highlights con las
 * vivas. El `deletedAt` del grupo es el MÁS RECIENTE de sus filas (todas se
 * borran juntas, pero ante datos legados dispares gana el último).
 */
export function groupTrashedHighlightRows(rows: TrashedHighlightRow[]): TrashedHighlightEntry[] {
  const groups = new Map<string, TrashedHighlightEntry>();

  for (const row of rows) {
    if (!row.highlightId) {
      continue;
    }
    const key = row.originId ?? row.highlightId;
    let group = groups.get(key);
    if (!group) {
      group = {
        kind: "highlight",
        id: key,
        originId: row.originId,
        rowIds: [],
        color: row.color ?? DEFAULT_HIGHLIGHT_COLOR,
        deletedAt: row.deletedAt,
        verses: [],
      };
      groups.set(key, group);
    }
    group.rowIds.push(row.highlightId);
    if (row.deletedAt && (!group.deletedAt || row.deletedAt > group.deletedAt)) {
      group.deletedAt = row.deletedAt;
    }
    if (row.book !== null && row.chapter !== null && row.verse !== null) {
      group.verses.push({
        version: row.version ?? "kjv",
        book: row.book,
        chapter: row.chapter,
        verse: row.verse,
      });
    }
  }

  const list = [...groups.values()];
  for (const group of list) {
    group.verses.sort(compareVerseRefs);
  }
  return list;
}

/**
 * Mezcla las tres clases en una sola lista ordenada por `deletedAt`
 * descendente (lo borrado más recientemente arriba); entradas sin fecha van al
 * final. Empates: orden estable por id para render determinista.
 */
export function mergeTrashEntries(
  notes: TrashedNoteEntry[],
  highlights: TrashedHighlightEntry[],
  bookmarks: TrashedBookmarkEntry[],
): TrashEntry[] {
  const all: TrashEntry[] = [...notes, ...highlights, ...bookmarks];
  return all.toSorted((a, b) => {
    if (a.deletedAt && b.deletedAt && a.deletedAt !== b.deletedAt) {
      return b.deletedAt.localeCompare(a.deletedAt);
    }
    if (Boolean(a.deletedAt) !== Boolean(b.deletedAt)) {
      return a.deletedAt ? -1 : 1;
    }
    return a.id.localeCompare(b.id);
  });
}
